<script>
export default {
	name: 'NavigationTiles',
	inheritAttrs: false,
};
</script>

<script setup>
import AppHyperlink from '~/components/AppHyperlink.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';

const props = defineProps({
	title: { type: String, default: '' },
	description: { type: String, default: '' },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	imageMultiList: { type: Array, default: () => ([]) },
	ctaMultiList: { type: Array, default: () => ([]) },
	componentIdAA: { type: String, default: '' },
});
const emit = defineEmits([
	// 'update:modelValue',
]);

const { isEditorMode } = useGlobalAEMState();

const siteName = window.siteName;

</script>

<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>

<template v-else>
	<div
		class="NavigationTiles generic-container"
		:data-use-theme="siteName"
		:class="{ '>md:hidden': isHideInDesktop, 'hidden >md:block': isHideInMobile }"
	>
		<div class="text-left rtl:text-right">
			<h4 class="navigation-title">{{ props.title }}</h4>
			<div>
				<p
					v-html-sanitize="props.description"
					class="navigation-desc"
				></p>
			</div>
		</div>
		<div
			v-if="props.imageMultiList"
			class="grid gap-4 pb-6 md:grid-cols-1"
			:class="props.imageMultiList.length <= 2 ? 'grid-cols-2' : `grid-cols-${props.imageMultiList.length}`"
		>
			<AppHyperlink
				v-for="(item, index) in props.imageMultiList"
				:key="index"
				v-aa="[
					{
						clickName: item.ctaLabel,
						clickComponentType: 'Button',
						componentName: 'NavigationTiles',
						componentID: props.componentIdAA,
					},
					{
						type: isUrlMatchCurrentHostname(item.ctaUrl) ? 'other' : 'exit',
						name: item.ctaLabel,
					},
				]"
				class="image-column-wrapper group rounded-xl flex flex-col-reverse h-full relative"
				:href="item.ctaUrl"
				:target="item.isOpenInNewTab ? '_blank' : '_self'"
				:showExternalLinkIcon="false"
				style="--focus-visible-outline-offset: 0px;"
			>
				<div
					style="background-image: var(--navTileBgGradient)"
					class="absolute z-20 w-full h-full rounded-xl"
				>
				</div>
				<img
					class="h-full w-full object-cover object-center rounded-xl pointer-events-none"
					:src="isMobileViewport ? item.tileMobileImage : item.tileDesktopImage"
					:alt="isMobileViewport ? item.tileMobileImageAltText : item.tileDesktopImageAltText"
				/>
				<div class="px-5 pb-5 flex absolute bottom-0 justify-between items-end text-neutral-white-base z-20 w-full">
					<h5 class="line-clamp-2">{{ item.ctaLabel }}</h5>
					<icon-fas-arrow-right class="icon-arrow-right mb-0.5 min-w-6 ml-4 fill-neutral-white-base rtl:(mr-4 ml-0)" />
				</div>
			</AppHyperlink>
		</div>
		<div v-if="props.ctaMultiList" class="grid grid-cols-2 gap-4 md:grid-cols-1">
			<AppHyperlink
				v-for="(item, index) in props.ctaMultiList"
				:key="index"
				v-aa="[
					{
						clickName: item.ctaLabel,
						clickComponentType: 'Button',
						componentName: 'NavigationTiles',
						componentID: props.componentIdAA,
					},
					{
						type: isUrlMatchCurrentHostname(item.ctaUrl) ? 'other' : 'exit',
						name: item.ctaLabel,
					},
				]"
				:href="item.ctaUrl"
				:target="item.isOpenInNewTab ? '_blank' : '_self'"
				:showExternalLinkIcon="false"
				class="navigation-text-tiles group !flex-nowrap !no-underline"
				style="--focus-visible-outline-offset: 0px;"
			>
				<span class="line-clamp-2 text-base">{{ item.ctaLabel }}</span>
				<icon-fas-arrow-right
					v-if="!item.isExternalLink"
					class="icon-arrow-right text-link-icon"
				/>
				<icon-far-arrow-up-right-from-square
					v-else
					class="icon-external-link text-link-icon"
				/>
			</AppHyperlink>
		</div>
	</div>
</template>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

// ---------------------- Text Tiles ----------------------
.NavigationTiles {
	--navTileBgGradient: linear-gradient(to bottom, rgba(31, 64, 116, 0.1), rgba(31, 64, 116, 0.5) 50%, rgba(31, 64, 116, 1) 90%);

	@apply p-10 border border-solid rounded-3xl;

	@media #{var.$query-max-md} {
		@apply py-8 px-5;
	}
}

.navigation-title {
	@apply pb-4;

	@media #{var.$query-max-md} {
		@apply pb-3;
	}
}

.navigation-desc {
	@apply pb-8;

	@media #{var.$query-max-md} {
		@apply pb-6;
	}
}

// ---------------------- Text Tiles ----------------------
.navigation-text-tiles {
	@apply text-left rounded-xl text-primary-blue-base fill-primary-blue-base bg-primary-blue-extralight py-4 px-5;

	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border: transparent;

	&:hover {
		.icon-arrow-right {
			@apply transform translate-x-0.5;

			html[dir="rtl"] & {
				@apply transform -translate-x-0.5 -rotate-180;
			}
		}

		.icon-external-link {
			@apply transform translate-x-0.5;

			html[dir="rtl"] & {
				@apply transform -translate-x-0.5 -rotate-90;
			}
		}
	}
}

.image-column-wrapper {
	height: 200px;

	&:hover {
		.icon-arrow-right {
			@apply transform translate-x-0.5;

			html[dir="rtl"] & {
				@apply transform -translate-x-0.5 -rotate-180;
			}
		}

		.icon-external-link {
			@apply transform translate-x-0.5;

			html[dir="rtl"] & {
				@apply transform -translate-x-0.5 -rotate-90;
			}
		}
	}
	
	@media #{var.$query-max-md} {
		height: 128px;
	}
}

.icon-arrow-right {
	min-width: 24px;
	min-height: 24px;
	transition: transform .3s ease-in-out;
	
	html[dir="rtl"] & {
		@apply transform -rotate-180;
	}
	
	@media #{var.$query-max-md} {
		min-width: 16px;
		min-height: 16px;
	}
}

.text-base {
	@apply group-hover:text-primary-blue-light group-active:text-secondary-blue-extradark;
}
.text-link-icon {
	@apply ml-2 rtl:(ml-0 mr-2) group-hover:(fill-primary-blue-light) group-active:fill-secondary-blue-extradark;
	margin-top: 2px;
}

.icon-external-link {
	min-width: 24px;
	min-height: 24px;
	transition: transform .3s ease-in-out;

	html[dir="rtl"] & {
		@apply transform -rotate-90;
	}
	
	@media #{var.$query-max-md} {
		min-width: 16px;
		min-height: 16px;
	}
}

[data-use-theme="MAG"] {
	.navigation-text-tiles {
		color: var(--primary-midnightgreen-base);
		fill: var(--primary-midnightgreen-base);
		background-color: var(--primary-midnightgreen-extralight);
		
		&:hover {
			.text-link-icon {
				fill: var(--primary-midnightgreen-light);
			}

			.text-base {
				color: var(--primary-midnightgreen-light);
			}
		}

		&:active {
			.text-link-icon {
				fill: var(--secondary-midnightgreen-extradark);
			}

			.text-base {
				color: var(--secondary-midnightgreen-extradark);
			}
		}
	}
}

.NavigationTiles[data-use-theme="amal"] {

	&.NavigationTiles {
		--navTileBgGradient: linear-gradient(to bottom, rgba(31, 64, 116, 0.1), rgba(51, 51, 51, 0.5) 50%, rgba(51, 51, 51, 1) 90%);

		.icon-arrow-right {
			min-height: 18px;
			min-width: 18px;
		}
	}

	.navigation-text-tiles {
		color: var(--primary-amal-funblue-light);
		fill: var(--primary-amal-funblue-light);
		background-color: var(--neutral-grey-extralight);
		font-weight: 600;

		.text-link-icon {
			min-height: 18px;
			min-width: 18px;
		}
	}
}

.NavigationTiles[data-use-theme="firefly"] {
	--navTileBgGradient: linear-gradient(to bottom, rgba(243, 130, 32, 0.1), rgba(243, 130, 32, 0.5) 50%, rgba(243, 130, 32, 0.9) 90%);

	.navigation-text-tiles {
		color: var(--primary-firefly-orange-base);
		fill: var(--primary-firefly-orange-base);
		background-color: var(--secondary-firefly-orange-ultralight);
		
		&:hover {
			.text-link-icon {
				fill: var(--secondary-firefly-orange-extralight);
			}

			.text-base {
				color: var(--secondary-firefly-orange-extralight);
			}
		}

		&:active {
			.text-link-icon {
				fill: var(--secondary-firefly-orange-extradark);
			}

			.text-base {
				color: var(--secondary-firefly-orange-extradark);
			}
		}
	}
}

</style>
